import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import { Users, MessageSquare, ArrowRight, CheckCircle, Briefcase, GraduationCap, Loader2, Bot, Sparkles, CheckSquare } from "lucide-react";
import Layout from "../components/layout/Layout";
import ExpertCard from "../components/ui/ExpertCard";
import { useExperts } from "../hooks/useExperts";
import { useAuth } from "../context/AuthContext";

export default function HomePage() {
  const navigate = useNavigate();
  const { experts: dbExperts, loading: expertsLoading } = useExperts();
  const { user, login, register } = useAuth();

  // Auth Form State
  const [authMode, setAuthMode] = useState("register"); // 'register' | 'login'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [showCheckError, setShowCheckError] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  const featuredExperts = dbExperts.filter(e => e.available).slice(0, 3);

  const stats = [
    { value: "150+", label: "Участников" },
    { value: "2000+", label: "Обсуждений" },
    { value: "4.9", label: "Средняя оценка" },
    { value: "98%", label: "Рекомендуют" }
  ];

  const howItWorks = [
    {
      icon: Bot,
      title: "Выгрузите мысли ИИ-Помощнику",
      description: "Напишите ИИ-партнеру любую бизнес-идею, проблему или абстрактное поручение."
    },
    {
      icon: CheckSquare,
      title: "Он упакует их в задачи",
      description: "ИИ-Помощник задаст уточняющие вопросы и превратит хаос в четкую задачу с критериями результата и дедлайнами."
    },
    {
      icon: Users,
      title: "Делегируйте или позовите эксперта",
      description: "Назначьте задачу сотруднику в один клик. А если нужна глубина — призовите практика из реального бизнеса."
    }
  ];

  const values = [
    "ИИ-Помощник доступен 24/7 — обстучите идею в любой момент",
    "Живые эксперты и практики с реальным опытом в бизнесе",
    "Честная обратная связь, а не красивые слова",
    "Конкретные рекомендации, проверенные практикой",
    "ИИ фиксирует выводы — ничего не потеряется",
    "Конфиденциальность каждого разговора"
  ];

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError("");

    if (authMode === "register" && (!agreeTerms || !agreePrivacy)) {
      setShowCheckError(true);
      return;
    }
    setShowCheckError(false);
    setAuthLoading(true);

    try {
      if (authMode === "register") {
        await register(email, password, "Новый пользователь", "client");
        navigate("/onboarding");
      } else {
        await login(email, password);
        navigate("/workspace");
      }
    } catch (err) {
      setAuthError(err.message || "Произошла ошибка при авторизации");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleModeSwitch = () => {
    setAuthMode(authMode === "register" ? "login" : "register");
    setShowCheckError(false);
    setAuthError("");
  };

  return (
    <Layout>
      <section id="home_hero" className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 bg-neutral-950" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-primary-800/8 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-accent-800/6 rounded-full blur-[100px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            
            {/* Левая часть: Текст */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <p className="text-lg font-extralight text-primary-400 mb-6 tracking-wide">
                Ваш напарник в бизнес-решениях
              </p>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extralight text-neutral-100 mb-6 leading-tight text-balance">
                Вы не одиноки
                <br />
                <span className="text-primary-400 font-light">в своих вопросах</span>
              </h1>

              <p className="text-xl lg:text-2xl font-extralight text-neutral-400 mb-10 max-w-2xl leading-relaxed">
                Обстучите идею с личным ИИ-партнёром в любое время, а когда нужна глубина — подключайте экспертов и практиков из реального бизнеса, которые уже через это прошли.
              </p>
            </motion.div>

            {/* Правая часть: Форма авторизации */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              <div className="bg-neutral-900/60 border border-neutral-800 p-8 rounded-xl backdrop-blur-sm max-w-md ml-auto">
                {user ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-primary-400" />
                    </div>
                    <h3 className="text-2xl font-light text-neutral-100 mb-2">С возвращением!</h3>
                    <p className="text-neutral-400 font-extralight mb-8">Вы уже авторизованы в системе.</p>
                    <button
                      onClick={() => navigate("/workspace")}
                      className="w-full py-4 rounded-md bg-primary-700 hover:bg-primary-600 text-white text-lg font-light transition-colors"
                    >
                      Перейти в панель управления
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleAuthSubmit} className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-light text-neutral-100 mb-2">
                        {authMode === "register" ? "Начать работу" : "Войти в аккаунт"}
                      </h3>
                      <p className="text-neutral-400 font-extralight text-sm">
                        {authMode === "register" 
                          ? "Создайте профиль и начните формулировать задачи с ИИ-Помощником" 
                          : "Продолжите работу над своими задачами"}
                      </p>
                    </div>

                    <div className="space-y-4">
                      <input
                        type="email"
                        placeholder="Ваш Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-4 py-3 rounded-md bg-neutral-950 border border-neutral-800 text-neutral-100 font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-700 transition-colors"
                      />
                      <input
                        type="password"
                        placeholder="Пароль"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                        className="w-full px-4 py-3 rounded-md bg-neutral-950 border border-neutral-800 text-neutral-100 font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-700 transition-colors"
                      />
                    </div>

                    {authMode === "register" && (
                      <div className="space-y-3">
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <div className={`mt-1 flex-shrink-0 w-5 h-5 rounded border ${showCheckError && !agreeTerms ? 'border-red-500 bg-red-500/10' : 'border-neutral-700 group-hover:border-primary-600'} flex items-center justify-center transition-colors`}>
                            <input 
                              type="checkbox" 
                              className="sr-only" 
                              checked={agreeTerms}
                              onChange={(e) => {
                                setAgreeTerms(e.target.checked);
                                if(e.target.checked && agreePrivacy) setShowCheckError(false);
                              }}
                            />
                            {agreeTerms && <CheckCircle className="w-3.5 h-3.5 text-primary-500" />}
                          </div>
                          <span className={`text-sm font-extralight ${showCheckError && !agreeTerms ? 'text-red-400' : 'text-neutral-400'}`}>
                            Я согласен с <Link to="/terms" className="text-primary-400 hover:underline">Пользовательским соглашением</Link>
                          </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <div className={`mt-1 flex-shrink-0 w-5 h-5 rounded border ${showCheckError && !agreePrivacy ? 'border-red-500 bg-red-500/10' : 'border-neutral-700 group-hover:border-primary-600'} flex items-center justify-center transition-colors`}>
                            <input 
                              type="checkbox" 
                              className="sr-only" 
                              checked={agreePrivacy}
                              onChange={(e) => {
                                setAgreePrivacy(e.target.checked);
                                if(e.target.checked && agreeTerms) setShowCheckError(false);
                              }}
                            />
                            {agreePrivacy && <CheckCircle className="w-3.5 h-3.5 text-primary-500" />}
                          </div>
                          <span className={`text-sm font-extralight ${showCheckError && !agreePrivacy ? 'text-red-400' : 'text-neutral-400'}`}>
                            Я даю согласие на обработку персональных данных (152-ФЗ)
                          </span>
                        </label>
                      </div>
                    )}

                    {authError && (
                      <div className="p-3 rounded-md bg-red-900/20 border border-red-900/50 text-red-400 text-sm font-extralight">
                        {authError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={authLoading}
                      className={`w-full py-4 rounded-md flex items-center justify-center gap-2 text-lg font-light transition-colors ${
                        (authMode === 'register' && (!agreeTerms || !agreePrivacy))
                        ? "bg-neutral-800 text-neutral-500 cursor-not-allowed"
                        : "bg-primary-700 hover:bg-primary-600 text-white"
                      }`}
                    >
                      {authLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (authMode === "register" ? "Создать аккаунт" : "Войти")}
                      {!authLoading && <ArrowRight className="w-5 h-5" />}
                    </button>

                    <div className="text-center mt-6">
                      <button 
                        type="button" 
                        onClick={handleModeSwitch}
                        className="text-neutral-500 hover:text-neutral-300 text-sm font-extralight transition-colors"
                      >
                        {authMode === "register" ? "Уже есть аккаунт? Войти" : "Нет аккаунта? Зарегистрироваться"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="home_how-it-works" className="py-20 lg:py-28 bg-neutral-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-extralight text-neutral-100 mb-4">
              Как это работает
            </h2>
            <p className="text-xl font-extralight text-neutral-500 max-w-2xl mx-auto">
              Три шага до результата и порядка в делах
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative p-8 rounded-md bg-neutral-900/40 border border-neutral-800/30 text-center"
              >
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-md bg-primary-700 flex items-center justify-center text-white text-lg font-extralight">
                  {index + 1}
                </div>
                <div className="w-14 h-14 mx-auto mb-6 rounded-md bg-primary-800/20 flex items-center justify-center">
                  <step.icon className="w-7 h-7 text-primary-400" />
                </div>
                <h3 className="text-xl font-light text-neutral-200 mb-3">{step.title}</h3>
                <p className="text-lg font-extralight text-neutral-500">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="home_community" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-lg font-extralight text-primary-400 mb-4 tracking-wide">
                Почему это работает
              </p>
              <h2 className="text-3xl lg:text-4xl font-extralight text-neutral-100 mb-6 text-balance">
                ИИ-Помощник + живой эксперт
              </h2>
              <p className="text-xl font-extralight text-neutral-400 mb-8 leading-relaxed">
                ИИ помогает структурировать мысли, декомпозировать инициативы и подсветить слепые зоны в любое время. А когда нужна глубина — подключайте практика из реального бизнеса, который проходил то же самое. Вместе они закрывают любую управленческую задачу.
              </p>
              <ul className="space-y-4">
                {values.map((value, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-accent-500 flex-shrink-0 mt-0.5" />
                    <span className="text-lg font-extralight text-neutral-400">{value}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800/30">
                    <Bot className="w-8 h-8 text-primary-500 mb-4" />
                    <h3 className="text-xl font-light text-neutral-200 mb-2">ИИ-Помощник</h3>
                    <p className="text-lg font-extralight text-neutral-500">
                      Доступен 24/7, структурирует мысли и подсвечивает риски
                    </p>
                  </div>
                  <div className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800/30">
                    <Users className="w-8 h-8 text-accent-500 mb-4" />
                    <h3 className="text-xl font-light text-neutral-200 mb-2">Эксперты</h3>
                    <p className="text-lg font-extralight text-neutral-500">
                      Консультанты с глубокой экспертизой в своей области
                    </p>
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800/30">
                    <Briefcase className="w-8 h-8 text-warm-500 mb-4" />
                    <h3 className="text-xl font-light text-neutral-200 mb-2">Практики из бизнеса</h3>
                    <p className="text-lg font-extralight text-neutral-500">
                      Предприниматели, которые прошли через те же вызовы
                    </p>
                  </div>
                  <div className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800/30">
                    <Sparkles className="w-8 h-8 text-primary-400 mb-4" />
                    <h3 className="text-xl font-light text-neutral-200 mb-2">Результат</h3>
                    <p className="text-lg font-extralight text-neutral-500">
                      Конкретный план действий, а не абстрактные советы
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="home_stats" className="py-16 bg-neutral-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="text-3xl lg:text-4xl font-light text-neutral-200 mb-2">
                  {stat.value}
                </div>
                <div className="text-lg font-extralight text-neutral-500">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="home_featured-experts" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12"
          >
            <div>
              <h2 className="text-3xl lg:text-4xl font-extralight text-neutral-100 mb-4">
                Каталог экспертов (Премиум)
              </h2>
              <p className="text-xl font-extralight text-neutral-500">
                Эксперты и практики с проверенным опытом для решения сложных задач
              </p>
            </div>
            <Link
              to="/experts"
              className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 text-lg font-extralight transition-colors"
            >
              Все участники
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertsLoading ? (
              <div className="col-span-full text-center py-12">
                <Loader2 className="w-8 h-8 text-primary-500 animate-spin mx-auto" />
              </div>
            ) : (
              featuredExperts.map((expert) => (
                <ExpertCard key={expert.id} expert={expert} />
              ))
            )}
          </div>
        </div>
      </section>

    </Layout>
  );
}
